import { engineComponent, NumberUtils } from "neo-common";
import { BaseController } from "./BaseController";
import { Machine } from "../Machine";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { GameManager } from "../../Managers/GameManager";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { CustomEase } from "../../Components/Effects/CustomEase";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { SceneHolderElement } from "../../CustomComponents/SceneHolderElement";
import { ImageTextCounterElement } from "../../Components/Elements/Texts/ImageTextCounterElement";
import { BetManager } from "../../Managers/BetManager";
import { AppContext } from "../../../Common/AppContext";
import { Settings } from "../../CustomComponents/Settings";
import { Game } from "neo-games/Engine/Communication/index";
import { TimelineElement } from "../../Components/Elements/Graphics/TimelineElement";
import { AmbientBackground } from "../../Utils/AmbientBackground";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    private _controller: BaseController;

    private _wasFinished: boolean = false;

    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;

    private _background: SwitchElement;
    private _respinIntro: TimelineElement;
    private _respinOutro: TimelineElement;
    private _noWinOverlay: SwitchElement;

    private _respinCounter: number;
    private _respinOutroDelay: FrameTimerEventEmitter;
    private _respinStatus: number;
    private _respinSong: string;

    constructor() {
        super();

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._respinOutroDelay = CreateFrameTimer("respinOutroDelay");
        this._respinOutroDelay.on(FrameTimerEvents.Complete, this.respinOutroFinished.bind(this));

        this._respinCounter = 0;
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimatorExtended) {
                    this._winAnimator = this.childs[key];
                }

                if (key == "NoWinOverlay" && this.childs[key] instanceof SwitchElement) {
                    this._noWinOverlay = this.childs[key];
                }

                if (key == "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (key == "RespinIntro" && this.childs[key] instanceof TimelineElement) {
                    this._respinIntro = this.childs[key];
                }

                if (key == "RespinOutro" && this.childs[key] instanceof TimelineElement) {
                    this._respinOutro = this.childs[key];
                    this._respinOutro.on("finished", this.onRespinOutroAnimationFinished.bind(this));
                }
            }

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }
            }
        }

        //create own custom easing functions we used in game
        CustomEase._createOwnEasing();

        //added elements need for perspective zoom effect
        this.setupSceneElement();
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.baseResponse.initData.reels);

                        if (
                            GameManager.baseResponse.initData.customData &&
                            GameManager.baseResponse.initData.customData["respinReels"]
                        ) {
                            this._machine.initializeRespinReels(
                                GameManager.baseResponse.initData.customData["respinReels"],
                            );
                        }
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(
                                WinAnimatorExtended.FinishWinWithoutMysteryCalculation,
                                this.finishWinWithoutMysteryCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimatorExtended.FinishRespinWinAnimator,
                                this.finishRespinWinAnimator.bind(this),
                            );
                            this._winAnimator.on(WinAnimatorExtended.MysteryStart, this.mysteryStart.bind(this));
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["NoWinContainer"],
                                this.childs["WinContainer"],
                            );
                            this._winAnimator.initializeWilds(
                                this.childs["WildsContainer"],
                                this.childs["MysteryContainer"],
                            );
                            this._winAnimator.initializeMystery(this.childs["MysteryAnimator"]);
                            this._winAnimator.initializePaysBothWays(this.childs["PaysBothWays"]);
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }

                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }
    }

    initIntro(): void {
        if (GameManager.gameResponse.totalWin > 0 && !Settings.isRespin()) {
            if (GameManager.baseResponse.currentWin === 0) {
                Settings.InRespin = true;
                this.respinOutro();
            } else {
                PlayList.loop("SND_BaseRecapLoop");
            }
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        if (GameManager.baseResponse.currentWin > 0) {
            this._noWinOverlay.goToCase("WIN");
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        let paysBothWays = <SwitchElement>this.childs["PaysBothWays"];
        if (paysBothWays.getCurrentCase() == "LEFT" || paysBothWays.getCurrentCase() == "RIGHT") {
            paysBothWays.goToCase("BASE");
        }

        if (this._noWinOverlay.getCurrentCase() == "WIN") {
            this._noWinOverlay.goToCase("WIN_OUT");
        }

        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop(this._respinSong);
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        this._winAnimator.addLimitWildsToReels();
        Settings.InRespin = false;
        this._respinCounter = 0;

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        let paysBothWays = <SwitchElement>this.childs["PaysBothWays"];
        if (paysBothWays.getCurrentCase() == "LEFT" || paysBothWays.getCurrentCase() == "RIGHT") {
            paysBothWays.goToCase("BASE");
        }

        if (this._noWinOverlay.getCurrentCase() == "WIN") {
            this._noWinOverlay.goToCase("WIN_OUT");
        }

        this._machine.spinStart(this._winAnimator.reset(true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    machineStopped(): void {
        this._wasFinished = false;

        if (GameManager.baseResponse.currentWin > 0) {
            this._noWinOverlay.goToCase("WIN_IN");
        }

        if (this._respinCounter > 0) {
            this._respinStatus = this._winAnimator.getWildStatus();
            this.playRespinSound();

            if (!Settings.isRespin() && GameManager.baseResponse.currentWin == 0) {
                this.respinOutro();
            }
        }

        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop(this._respinSong);
            this._respinSong = "highwin";
        }
    }

    finishWinWithoutMysteryCalculation(): void {
        this._controller.winCalculationWithoutMysteryFinished();
    }

    finishWinCalculation(): void {
        if (!Settings.InRespin) {
            this._wasFinished = true;
        }

        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay && !Settings.isRespin()) {
            PlayList.stop(this._respinSong);
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");
    }

    outro(): void {
        PlayList.stop(this._respinSong, 15);
        PlayList.stop("SND_BaseLoop", 15);
        PlayList.stop("SND_BaseRecapLoop", 15);

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        if (this._noWinOverlay.getCurrentCase() == "WIN") {
            this._noWinOverlay.goToCase("WIN_OUT");
        }

        let paysBothWays = <SwitchElement>this.childs["PaysBothWays"];
        if (paysBothWays.getCurrentCase() == "LEFT" || paysBothWays.getCurrentCase() == "RIGHT") {
            paysBothWays.goToCase("BASE");
        }

        let inHighWins = this._winAnimator.reset(true);

        if (this._wasFinished) {
            this.outroFinished();
        } else {
            if (inHighWins) {
                this._outroDelay.start(60);
            } else {
                this._outroDelay.start(30);
            }
        }
    }

    outroFinished(): void {
        AmbientBackground.changeAmbientBackground("gambleBg", 100);

        this._controller.outroFinished();
    }

    selectPayline(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.select();
        }
    }

    private setupSceneElement(): void {
        SceneHolderElement.AddElement({
            element: this.childs["ReelsBg"],
            parent: this,
            index: this.getChildIndex(this.childs["ReelsBg"]),
        });
        SceneHolderElement.AddElement({
            element: this.childs["NoWinContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["NoWinContainer"]),
        });
        SceneHolderElement.AddElement({
            element: this.childs["NoWinOverlay"],
            parent: this,
            index: this.getChildIndex(this.childs["NoWinOverlay"]),
        });
        SceneHolderElement.AddElement({
            element: this.childs["Reels"],
            parent: this,
            index: this.getChildIndex(this.childs["Reels"]),
        });
        SceneHolderElement.AddElement({
            element: this.childs["WinContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["WinContainer"]),
        });
        SceneHolderElement.AddElement({
            element: this.childs["Logo"],
            parent: this,
            index: this.getChildIndex(this.childs["Logo"]),
        });
        SceneHolderElement.AddElement({
            element: this.childs["WildsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["WildsContainer"]),
        });
        SceneHolderElement.AddElement({
            element: this.childs["MysteryContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["MysteryContainer"]),
        });
        SceneHolderElement.AddElement({
            element: this.childs["Paylines"],
            parent: this,
            index: this.getChildIndex(this.childs["Paylines"]),
        });
    }

    /* RESPIN */

    respin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        if (this._noWinOverlay.getCurrentCase() == "WIN") {
            this._noWinOverlay.goToCase("WIN_OUT");
        }

        Settings.InRespin = true;
        this._respinCounter++;

        this._machine.spinStart(this._winAnimator.reset(true), true, this._winAnimator.currentSymbols);
        this._winAnimator.moveWilds();
    }

    respinIntro(): void {
        this._background.goToCase("RESPIN_IN");
        this._respinIntro.play();

        PlayList.stop("SND_BaseLoop");

        PlayList.play("SND_RespinIntro");
        PlayList.play("SND_RespinIntroSfx");

        this._respinStatus = this._winAnimator.getWildStatus();
        this.playRespinSound();

        AmbientBackground.changeAmbientBackground("respinBg", 300, 1000);
    }

    initRespinIntro(): void {
        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");

        this._background.goToCase("RESPIN");
        let respinText = <SwitchElement>this._background.getChildElementByName("RespinText");
        respinText.goToCase("RESPIN_IDLE");

        this._respinStatus = this._winAnimator.getWildStatus();
        this.playRespinSound();

        AmbientBackground.changeAmbientBackground("respinBg", 0);
    }

    finishRespinWinAnimator(): void {
        if (this._respinSong == "") {
            this.playRespinSound();
        } else if (this._respinSong == "highwin") {
            this.playRespinSound(false);
        }

        this._controller.allRespinWinsFinished();

        if (!Settings.isRespin()) {
            this.respinOutro();
        } else {
            this.finishWinAnimator();
        }
    }

    respinOutro(): void {
        let longOutro = false;

        //show respin end message only if there is win
        if (GameManager.baseResponse.totalWin > 0) {
            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                this._respinOutro.play(
                    parseInt(NumberUtils.devide(GameManager.baseResponse.totalWin, BetManager.CoinSize).toString()),
                );
            } else {
                ImageTextCounterElement.Round = false;
                this._respinOutro.play(GameManager.baseResponse.totalWin);
            }

            PlayList.play("SND_RespinWin");
            longOutro = true;
        }

        PlayList.stop(this._respinSong, 120);
        this._respinSong = "";

        if (longOutro) {
            this._background.goToCase("RESPIN_OUT_DELAYED");
            let respinText = <SwitchElement>this._background.getChildElementByName("RespinText");

            if (respinText.getCurrentCase().indexOf("RESPIN_IDLE") == -1) {
                respinText.goToCase("RESPIN_IDLE");
            }
            this._respinOutroDelay.start(WinAnimatorExtended.RespinOutroDelay);

            AmbientBackground.changeAmbientBackground(
                "base",
                500,
                (WinAnimatorExtended.RespinOutroDelay / 60) * 1000 - 500,
            );
        } else {
            this._background.goToCase("RESPIN_OUT");
            this._respinOutroDelay.start(30);

            AmbientBackground.changeAmbientBackground("base", 500);
        }
    }

    private onRespinOutroAnimationFinished(): void {
        this._wasFinished = true;

        this._controller.winCalculationFinished();
    }

    respinOutroFinished(): void {
        this._respinOutroDelay.stop();
        // PlayList.loop('SND_BaseLoop', 60);
        PlayList.stop("SND_BaseLoop");
        PlayList.loop("SND_BaseRecapLoop");

        if (GameManager.baseResponse.currentWin > 0) {
            this.finishWinAnimator();
        } else {
            if (GameManager.baseResponse.totalWin > 0) {
                this._controller.allWinsFinished();
            }
        }
    }

    /* RESPIN SOUNDS */

    private playRespinSound(intro: boolean = true): void {
        let lastPlayed = this._respinSong;
        switch (this._respinStatus) {
            case 0:
                this._respinSong = "SND_RespinSheriff";
                break;
            case 1:
                this._respinSong = "SND_RespinRogue";
                break;
            case 2:
                this._respinSong = "SND_RespinBoth";
                break;
            case 3:
                this._respinSong = "SND_RespinMystery";
                break;
        }

        if (lastPlayed != this._respinSong) {
            if (lastPlayed != "") {
                PlayList.stop(lastPlayed, 30);
            }

            if (lastPlayed != "SND_RespinBoth" && intro) {
                PlayList.play(this._respinSong + "Intro");
            }
            PlayList.loop(this._respinSong, 30);
        }
    }

    /* MYSTERY */

    mysteryStart(): void {
        PlayList.stop(this._respinSong);
        this._respinSong = "";
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/